import { Amplify } from "aws-amplify";
import { confirmSignIn, fetchAuthSession, signIn, signOut, signUp } from "aws-amplify/auth";
import { cognitoUserPoolsTokenProvider } from "aws-amplify/auth/cognito";
import { v4 as uuid } from "uuid";

import { APP_V2_APP_EVENTS, logError } from "../../lib/analytics";
import { cognitoConfig } from "./config";

Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: cognitoConfig.userPoolId,
      userPoolClientId: cognitoConfig.clientId,
    },
  },
});

const COGNITO_STORAGE_PREFIX = "CognitoIdentityServiceProvider";

cognitoUserPoolsTokenProvider.setKeyValueStorage({
  async getItem(key: string): Promise<string | null> {
    return localStorage.getItem(key);
  },
  async setItem(key: string, value: string): Promise<void> {
    localStorage.setItem(key, value);
  },
  async removeItem(key: string): Promise<void> {
    localStorage.removeItem(key);
  },
  async clear(): Promise<void> {
    const keysToRemove: string[] = [];
    for (let index = 0; index < localStorage.length; index++) {
      const key = localStorage.key(index);
      if (key?.startsWith(COGNITO_STORAGE_PREFIX)) {
        keysToRemove.push(key);
      }
    }
    for (const key of keysToRemove) {
      localStorage.removeItem(key);
    }
  },
});

export interface CognitoUserCredential {
  idToken: string;
  accessToken?: string;
}

const CHALLENGE_TYPE = {
  MAGIC_LINK: "MAGIC_LINK",
} as const;

type ChallengeType = (typeof CHALLENGE_TYPE)[keyof typeof CHALLENGE_TYPE];

function getAppRedirectUrl(email: string): string {
  const { port, hostname, pathname, search } = globalThis.location;
  const isLocalhost = hostname === "localhost";
  const formattedPort = port === "" ? "" : `:${port}`;
  const appDomain = `http${isLocalhost ? "" : "s"}://${hostname}${formattedPort}`;
  const prPrefix = /^\/pr-\d+/.exec(pathname)?.at(0) ?? "";
  const searchParams = new URLSearchParams({ email });
  const requestedUrl = new URLSearchParams(search).get("requestedUrl");
  if (requestedUrl) {
    searchParams.set("requestedUrl", requestedUrl);
  }
  return `${appDomain}${prPrefix}/v2/email-login-link?${searchParams.toString()}`;
}

/**
 * Triggers the PreSignUp Lambda to JIT-create the Cognito user if needed.
 * The signup is always rejected by the Lambda — errors are intentionally ignored.
 */
async function ensureCognitoUser(email: string): Promise<void> {
  try {
    // Password must satisfy the Cognito user pool policy (uppercase, lowercase,
    // number, special char) so the request reaches the PreSignUp Lambda.
    const password = `P@0a${uuid()}`;
    await signUp({ username: uuid(), password, options: { userAttributes: { email } } });
  } catch (error: unknown) {
    // The PreSignUp Lambda always rejects admin-app signups, so most errors here
    // are expected. However, if the Lambda specifically threw "USER_NOT_FOUND",
    // it means the user does not exist in the backend — surface this to the caller
    // so we fail fast instead of proceeding to initiateCustomAuth (which would fail
    // with a misleading NotAuthorizedException).
    const errorName =
      error && typeof error === "object" && "name" in error
        ? (error as { name?: unknown }).name
        : undefined;
    const errorMessage =
      error && typeof error === "object" && "message" in error
        ? (error as { message?: unknown }).message
        : undefined;

    if (
      errorName === "UserLambdaValidationException" &&
      typeof errorMessage === "string" &&
      errorMessage.includes("USER_NOT_FOUND")
    ) {
      const userNotFoundError = new Error(
        "No registered user found for the provided email address."
      );
      (userNotFoundError as { name: string }).name = "UserNotFoundException";
      throw userNotFoundError;
    }
    // All other errors are expected (Lambda rejecting signup) — ignore them.
  }
}

function isUserAlreadyAuthenticatedException(error: unknown): boolean {
  return (
    error !== null &&
    typeof error === "object" &&
    "name" in error &&
    (error as { name: string }).name === "UserAlreadyAuthenticatedException"
  );
}

async function initiateCustomAuth(username: string): Promise<void> {
  try {
    await signIn({
      username,
      options: {
        authFlowType: "CUSTOM_WITHOUT_SRP",
        clientMetadata: {
          "client_metadata:custom_challenge:select": "init",
        },
      },
    });
  } catch (error: unknown) {
    if (!isUserAlreadyAuthenticatedException(error)) {
      throw error;
    }

    // A previous Cognito session still exists (tokens in localStorage).
    // Clear it and retry so the new sign-in flow can proceed.
    await signOut();
    await signIn({
      username,
      options: {
        authFlowType: "CUSTOM_WITHOUT_SRP",
        clientMetadata: {
          "client_metadata:custom_challenge:select": "init",
        },
      },
    });
  }
}

async function selectMagicLinkChallenge(
  email: string,
  options?: { skipEmail?: boolean }
): Promise<void> {
  const clientMetadata: Record<string, string> = {
    target_custom_challenge: CHALLENGE_TYPE.MAGIC_LINK,
    sub_challenge_step: "select",
    requester_url: getAppRedirectUrl(email),
  };

  if (options?.skipEmail) {
    clientMetadata.skip_challenge_creation = "true";
  }

  const result = await confirmSignIn({
    challengeResponse: CHALLENGE_TYPE.MAGIC_LINK,
    options: {
      clientMetadata,
    },
  });

  if (result.nextStep.signInStep === "CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE") {
    const challengeParams = result.nextStep.additionalInfo ?? {};
    if (challengeParams.status === "error") {
      throw new Error(challengeParams.message ?? "Magic link challenge failed");
    }
  }
}

async function respondToChallenge(
  challengeAnswer: string,
  challengeType: ChallengeType
): Promise<CognitoUserCredential> {
  const confirmResult = await confirmSignIn({
    challengeResponse: challengeAnswer,
    options: {
      clientMetadata: {
        target_custom_challenge: challengeType,
        sub_challenge_step: "answer",
        [`client_metadata:custom_challenge:${challengeType.toLowerCase()}`]: "verify",
      },
    },
  });

  if (confirmResult.nextStep.signInStep === "CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE") {
    const challengeParams = confirmResult.nextStep.additionalInfo ?? {};
    if (challengeParams.status === "error") {
      throw new Error(challengeParams.message ?? "Challenge failed");
    }
  }

  if (confirmResult.isSignedIn) {
    const session = await fetchAuthSession();
    const idToken = session.tokens?.idToken?.toString();

    if (!idToken) {
      throw new Error("Missing Cognito id token after authentication");
    }

    return {
      idToken,
      accessToken: session.tokens?.accessToken?.toString(),
    };
  }

  throw new Error("Authentication not completed");
}

export async function requestMagicLink(email: string): Promise<void> {
  await ensureCognitoUser(email);
  await initiateCustomAuth(email);
  await selectMagicLinkChallenge(email);
}

export async function completeMagicLinkSignIn(params: {
  payload: string;
  email: string;
}): Promise<CognitoUserCredential> {
  const { payload, email } = params;

  // Re-initiate the full auth flow before responding with the magic link payload.
  // If a previous Cognito session exists, initiateCustomAuth will clear it automatically.
  await initiateCustomAuth(email);
  await selectMagicLinkChallenge(email, { skipEmail: true });

  return await respondToChallenge(payload, CHALLENGE_TYPE.MAGIC_LINK);
}

export async function cognitoSignOut(): Promise<void> {
  await signOut();
}

/**
 * Checks whether a valid Cognito session exists in localStorage.
 * Returns the email of the currently authenticated user, or undefined if no session exists.
 */
export async function getExistingSessionEmail(): Promise<string | undefined> {
  try {
    const session = await fetchAuthSession();
    const email = session.tokens?.idToken?.payload?.email;
    return typeof email === "string" ? email : undefined;
  } catch {
    return undefined;
  }
}

export async function getCognitoIdToken(forceRefresh?: boolean): Promise<string | undefined> {
  const session = await fetchAuthSession({ forceRefresh });
  return session.tokens?.idToken?.toString();
}

export function mapCognitoError(error: unknown): { code: string; message: string } {
  if (error && typeof error === "object" && "name" in error) {
    const amplifyError = error as { name: string; message: string };

    switch (amplifyError.name) {
      // When the DefineAuthChallenge Lambda throws an error (instead of returning
      // failAuthentication: true), Cognito surfaces it as a UserLambdaValidationException
      // with the Lambda's error message embedded. We use this to distinguish "user type
      // not allowed" from generic auth failures.
      case "UserLambdaValidationException": {
        if (amplifyError.message.includes("USER_TYPE_NOT_ALLOWED")) {
          return {
            code: "auth/user-type-not-allowed",
            message:
              "Your account type does not have access to this application. Please make sure you are using the correct app for your account.",
          };
        }
        logError(APP_V2_APP_EVENTS.COGNITO_AUTH_REDIRECT_FAILURE, {
          error,
          metadata: { errorName: amplifyError.name, errorMessage: amplifyError.message },
        });
        return {
          code: "auth/lambda-error",
          message: "An authentication error occurred.",
        };
      }
      case "UserNotFoundException": {
        return {
          code: "auth/user-not-found",
          message: "No registered user found for the provided email address.",
        };
      }
      // Cognito returns NotAuthorizedException for multiple reasons:
      // - "Incorrect username or password." during challenge response → expired/invalid link
      // - During signIn for a non-existent user in passwordless flows
      // Check the message to provide the right user-facing error.
      case "NotAuthorizedException": {
        if (amplifyError.message.includes("Incorrect username or password")) {
          return {
            code: "auth/invalid-link",
            message:
              "This sign-in link has expired or has already been used. Links are valid for 10 minutes. Please request a new one.",
          };
        }
        return {
          code: "auth/user-not-found",
          message: "No registered user found for the provided email address.",
        };
      }
      case "TooManyRequestsException": {
        return {
          code: "auth/too-many-requests",
          message: "Too many requests, please try again later",
        };
      }
      case "LimitExceededException": {
        return {
          code: "auth/limit-exceeded",
          message: "Attempt limit exceeded, please try after some time",
        };
      }
      case "NetworkError": {
        return { code: "auth/network-request-failed", message: "Network error occurred" };
      }
      default: {
        logError(APP_V2_APP_EVENTS.COGNITO_AUTH_REDIRECT_FAILURE, {
          error,
          metadata: { errorName: amplifyError.name, errorMessage: amplifyError.message },
        });
        return {
          code: "auth/unknown",
          message: amplifyError.message || "An unknown error occurred",
        };
      }
    }
  }

  logError(APP_V2_APP_EVENTS.COGNITO_AUTH_REDIRECT_FAILURE, {
    error: error instanceof Error ? error : new Error(String(error)),
    metadata: { errorMessage: String(error) },
  });
  return { code: "auth/unknown", message: "An unknown error occurred" };
}
